import React, { ReactNode, useMemo, useRef, useState } from "react";
import Cropper from "./Cropper";
import { Size } from "react-easy-crop";
import {
  calculateMaxDimensions,
  getAspectRatio,
  readFile,
  toBase64,
  hasAllowedFileType,
  partitionFilesByValidity,
} from "./helpers";
import ImageCropProvider, { useImageCropContext } from "./ImageCropProvider";
import { Button } from "../Button/button";
import { Icon } from "../Icon/icon";
import { Toggle } from "../Toggle";
import styles from "./ImageInput.module.scss";
import { StatusText } from "../StatusText/StatusText";
import { LoadingDots } from "../LoadingDots";

type ExportData = {
  previewUrl: string;
  encoded: string;
  raw: Blob;
};

// TODO: move a bunch of props into SingleImageMode that don't make sense to have when multiple is true
type CommonProps = {
  id: string;
  placeholder: ReactNode;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  translations: {
    uploadImageOverlayText: string;
    replaceImageAriaLabel: string;
    replaceImageOverlayText: string;
    undoChangeButtonText: string;
    backgroundToggleText?: string;
    editDoneText: string;
    editCancelText: string;
    fileTooLargeText: string;
    dropZoneText: string;
    rotateClockwiseAriaLabel?: string;
    rotateAntiClockwiseAriaLabel?: string;
  };
  /**
   * @default "crop"
   */
  strategy?: "crop" | "cover";
  originalImageUrl?: string;
  currImageUrl?: string;
  /**
   * `context.fileNames` lists the files a given error applies to. Errors are
   * reported once per reason with all offending files, not once per file.
   */
  onError: (
    error: string,
    type?: "warning",
    context?: { fileNames: string[] },
  ) => void;
  onUndo?: (originalImgUrl: string) => void;
  menuPlacement?: "top" | "bottom";
  cropShape?: "round" | "rect";
  aspectRatio?: number | "auto";
  showGrid?: boolean;
  cropSize?: Size; // sets specific dimensions which override aspect-ratio
  cropBounds?: Size; // sets dimension boundaries which will respect image's original aspect-ratio
  showFaceOverlayGuide?: boolean; // Show face overlay guide for profile photo cropping. When true, also disables grid on cropper
  faceOverlayTooltip?: React.ReactNode;
  style?: {
    overlayStyle?: React.CSSProperties;
    previewStyle?: {
      container?: React.CSSProperties;
      image?: React.CSSProperties;
    };
    containerStyle?: React.CSSProperties;
    mediaStyle?: React.CSSProperties;
    cropAreaStyle?: React.CSSProperties;
    menuStyle?: React.CSSProperties;
  };
  tips?: ReactNode;
  disabled?: boolean;
  removeBackground?: boolean;
  showRotateButton?: boolean;
};
type SingleImageMode = CommonProps & {
  multiple?: false;
  maxFiles?: never;
  onChange: (data: ExportData) => void;
};
type MultiImageMode = CommonProps & {
  /**
   * If true, allows multiple files to be uploaded
   * @warning only use this if you are handling multiple files in the onChange callback
   */
  multiple: true;
  maxFiles?: number;
  onChange: (data: ExportData[]) => void;
};
type ImageInputProps = SingleImageMode | MultiImageMode;

function InputComponent({
  id,
  placeholder,
  allowedFileTypes = [".png", ".svg", ".jpg", ".jpeg", ".webp"],
  maxFileSize = 6 * 1024 * 1024, // 6 MB in bytes
  translations,
  multiple = false,
  maxFiles,
  strategy = "crop",
  currImageUrl,
  originalImageUrl,
  onChange,
  onError,
  onUndo,
  menuPlacement = "bottom",
  cropShape,
  aspectRatio,
  showGrid,
  cropSize,
  cropBounds,
  showFaceOverlayGuide,
  faceOverlayTooltip,
  style,
  tips,
  disabled,
  removeBackground,
  showRotateButton,
}: ImageInputProps) {
  const context = useImageCropContext();
  if (!context)
    throw new Error(
      "<ImageInput /> is missing it's context. Check the <ImageCropProvider />",
    );
  const {
    zoom,
    setImage,
    getProcessedImage,
    resetStates,
    handleZoomIn,
    handleZoomOut,
    rotation,
    setRotation,
  } = context;

  const maxFileSizeMB = (maxFileSize / 1024 / 1024).toFixed(1);
  const [backgroundChecked, setBackgroundChecked] = useState(false);
  const [imageWithoutBackground, setImageWithoutBackground] = useState<
    string | undefined
  >(undefined);
  const [imageWithBackground, setImageWithBackground] = useState<
    string | undefined
  >(undefined);

  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [displayCropper, setDisplayCropper] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(currImageUrl);
  const [processing, setProcessing] = useState(false);
  const [inputKey, setInputKey] = useState(new Date().getTime().toString());
  const [computedAspectRatio, setComputedAspectRatio] = useState<number>();
  const [computedCropSize, setComputedCropSize] = useState<Size | undefined>(
    cropSize,
  );
  const [fileTooLarge, setFileTooLarge] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [displayRotateOnly, setDisplayRotateOnly] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | undefined>(undefined);
  const [imgNaturalSize, setImgNaturalSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  // Scale factor so the rotated image always fits within its original display box
  const rotateOnlyScale = useMemo(() => {
    if (!imgNaturalSize) return 1;
    const { w, h } = imgNaturalSize;
    const rad = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    const bboxW = w * cos + h * sin;
    const bboxH = w * sin + h * cos;
    return Math.min(w / bboxW, h / bboxH);
  }, [rotation, imgNaturalSize]);

  const handleCancel = () => {
    setPreview(originalImageUrl);
    resetStates();
    setDisplayCropper(false);
    setDisplayRotateOnly(false);
    setPendingFile(undefined);
  };

  const handleDone: (
    imageOverride?: File | FileList | File[],
  ) => Promise<void> = async (imageOverride) => {
    if (multiple) {
      if (imageOverride instanceof FileList || Array.isArray(imageOverride)) {
        // Process multiple images
        const failed: string[] = [];
        const processedImages: ExportData[] = await Promise.all(
          Array.from(imageOverride).map(async (file) => {
            const previewUrl = window.URL.createObjectURL(file);
            const encoded = await toBase64(file);
            if (!encoded || typeof encoded !== "string") {
              failed.push(file.name);
              return { previewUrl, encoded: "", raw: file };
            }
            return { previewUrl, encoded, raw: file };
          }),
        );
        // Report once for all failures rather than one notification per file.
        if (failed.length) {
          onError("IMAGE_PROCESSING_FAILED", undefined, { fileNames: failed });
        }
        (onChange as (data: ExportData[]) => void)(
          processedImages.filter((image) => image.encoded),
        );
      }
      return;
    }

    setProcessing(true);
    const image = imageOverride ?? (await getProcessedImage());
    if (!image || !(image instanceof Blob)) {
      onError("IMAGE_PROCESSING_FAILED");
      return;
    }
    const previewUrl = window.URL.createObjectURL(image);
    const encoded = await toBase64(image);
    if (!encoded || typeof encoded !== "string") {
      onError("IMAGE_PROCESSING_FAILED");
      return;
    }
    setPreview(previewUrl);
    resetStates();
    setDisplayCropper(false);

    (onChange as (data: ExportData) => void)({
      previewUrl,
      encoded,
      raw: image,
    });

    setProcessing(false);
  };

  const applyRotationToFile = async (
    file: File,
    rot: number,
  ): Promise<File> => {
    if (rot === 0) return file;
    const dataUrl = await readFile(file);
    if (typeof dataUrl !== "string") throw new Error("Failed to read file");
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Failed to load image"));
      image.src = dataUrl;
    });
    const rad = (rot * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    const newWidth = Math.ceil(img.width * cos + img.height * sin);
    const newHeight = Math.ceil(img.width * sin + img.height * cos);
    const canvas = document.createElement("canvas");
    canvas.width = newWidth;
    canvas.height = newHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No canvas context");
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rad);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, file.type),
    );
    if (!blob) throw new Error("Canvas toBlob failed");
    return new File([blob], file.name, { type: file.type });
  };

  const handleDoneRotateOnly = async () => {
    if (!pendingFile) return;
    setProcessing(true);
    try {
      const rotatedFile = await applyRotationToFile(pendingFile, rotation);
      const previewUrl = window.URL.createObjectURL(rotatedFile);
      const encoded = await toBase64(rotatedFile);
      const result: ExportData = {
        previewUrl,
        encoded: typeof encoded === "string" ? encoded : "",
        raw: rotatedFile,
      };
      setPreview(previewUrl);
      setDisplayRotateOnly(false);
      setPendingFile(undefined);
      resetStates();
      setProcessing(false);
      (onChange as (data: ExportData) => void)(result);
    } catch {
      onError("IMAGE_PROCESSING_FAILED");
      setProcessing(false);
    }
  };

  const handleFileChange = async ({
    target: { files },
  }: {
    target: { files: FileList | null };
  }) => {
    setInputKey(new Date().getTime().toString()); // reset input so same image can be uploaded again if needed
    setFileTooLarge(false);
    setImageWithoutBackground(undefined);
    setImageWithBackground(undefined);

    if (!files) return;

    setLoading(true);

    let filesArray = Array.from(files);

    if (maxFiles) {
      if (files && files.length > maxFiles) {
        onError("MAX_FILES_EXCEEDED", "warning");
        filesArray = filesArray.slice(0, maxFiles);
      }
    }

    // If multiple is on, skip the cropper and just return the data. Files are
    // still validated here so a rejected file is reported while the user is
    // picking it, rather than failing much later during submit.
    if (multiple) {
      const { accepted, wrongType, tooLarge } = partitionFilesByValidity(
        filesArray,
        allowedFileTypes,
        maxFileSize,
      );

      // One notification per reason, listing every file it applies to.
      if (wrongType.length) {
        onError("INVALID_FILE_TYPE", undefined, {
          fileNames: wrongType.map((file) => file.name),
        });
      }
      if (tooLarge.length) {
        onError("IMAGE_TOO_BIG", undefined, {
          fileNames: tooLarge.map((file) => file.name),
        });
      }

      if (accepted.length) handleDone(accepted);
      setLoading(false);
      return;
    }

    const file = filesArray[0];
    if (!file) throw new Error("Uploaded file is null");

    if (!hasAllowedFileType(file, allowedFileTypes)) {
      onError("INVALID_FILE_TYPE", undefined, { fileNames: [file.name] });
      return;
    }
    if (file.size > maxFileSize) {
      setFileTooLarge(true);
      return;
    }

    if (strategy === "crop") {
      const imageDataUrl = await readFile(file);
      if (typeof imageDataUrl !== "string") {
        onError("UPLOAD_IMAGE_FAILED");
        return;
      }
      if (aspectRatio === "auto") {
        const aspectRatio = await getAspectRatio(imageDataUrl);
        if (cropBounds) {
          setComputedCropSize(
            calculateMaxDimensions(
              aspectRatio,
              cropBounds.width,
              cropBounds.height,
            ),
          );
        } else {
          setComputedAspectRatio(aspectRatio);
        }
      }

      if (removeBackground) {
        try {
          const imgly = await import("@imgly/background-removal");
          const removeBackgroundFunction = imgly.removeBackground;
          const imageBlob = await removeBackgroundFunction(imageDataUrl, {
            device: "gpu",
          });
          const blobToDataURL = (blob: Blob): Promise<string> => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                const res = e.target?.result;
                if (!res || typeof res !== "string") {
                  reject(new Error("Failed to read blob as data URL"));
                  return;
                }
                resolve(res);
              };
              reader.onerror = () =>
                reject(reader.error ?? new Error("FileReader error"));
              reader.readAsDataURL(blob);
            });
          };
          const result = await blobToDataURL(imageBlob);
          setImage(result);
          setImageWithoutBackground(result);
          setImageWithBackground(imageDataUrl);
        } catch {
          setImage(imageDataUrl);
        }
      } else {
        setImage(imageDataUrl);
      }
      setDisplayCropper(true);
    } else {
      if (showRotateButton) {
        setPreview(window.URL.createObjectURL(file));
        setPendingFile(file);
        setImgNaturalSize(null);
        setDisplayRotateOnly(true);
        setLoading(false);
        return;
      }
      await handleDone(file);
    }
    setLoading(false);
  };

  const handleDrop = async (
    e: React.DragEvent<HTMLLabelElement | HTMLDivElement>,
  ) => {
    stopPropagation(e);
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange({ target: { files: droppedFiles } });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    stopPropagation(e);
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    stopPropagation(e);
    // PROBLEM: onDragLeave is triggered when the user drags over children elements of the container
    // SOLUTION: Check if we actually left the entire container by checking the relatedTarget
    if (
      containerRef.current &&
      e.relatedTarget &&
      containerRef.current.contains(e.relatedTarget as Node)
    ) {
      return;
    }
    setIsDragging(false);
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`${styles.container} ${disabled ? styles.disabled : ""}`}
        style={style?.containerStyle}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        {isDragging && (
          <div className={styles.dropZone} style={style?.overlayStyle}>
            <Icon name="upload" />
            <p>
              <strong>{translations.dropZoneText}</strong>
            </p>
            <p className={styles.allowedImageFormats}>
              ({allowedFileTypes.join(", ")})
            </p>
            <p className={styles.maxFileSize}>Max {maxFileSizeMB} MB</p>
          </div>
        )}
        {displayCropper || displayRotateOnly ? (
          <>
            {displayRotateOnly && preview && (
              <div
                style={{
                  ...style?.previewStyle?.container,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  width: "100%",
                }}
              >
                <img
                  aria-hidden
                  src={preview}
                  onLoad={(e) =>
                    setImgNaturalSize({
                      w: e.currentTarget.naturalWidth,
                      h: e.currentTarget.naturalHeight,
                    })
                  }
                  style={{
                    ...style?.previewStyle?.image,
                    objectFit: "contain",
                    transform: `rotate(${rotation}deg) scale(${rotateOnlyScale})`,
                    transition: "transform 0.2s ease",
                  }}
                />
              </div>
            )}
            {displayCropper && (
              <Cropper
                cropShape={cropShape}
                aspectRatio={
                  aspectRatio === "auto" ? computedAspectRatio : aspectRatio
                }
                showGrid={showFaceOverlayGuide ? false : showGrid}
                cropSize={computedCropSize}
                showFaceOverlayGuide={showFaceOverlayGuide}
                faceOverlayTooltip={faceOverlayTooltip}
                style={{
                  ...style,
                  cropAreaStyle: {
                    ...style?.cropAreaStyle,
                    border: "1px solid black",
                  },
                }}
              />
            )}
            <div
              className={styles.imageControls}
              style={{
                ...style?.menuStyle,
                ...(menuPlacement === "top"
                  ? {
                      top: 0,
                      transform:
                        "translateY(calc(-100% - var(--wlcm-spacing-xs)))",
                    }
                  : undefined),
              }}
            >
              {!displayRotateOnly && (
                <div className={styles.zoomControls}>
                  <Button
                    dark
                    onClick={(e) => {
                      stopPropagation(e);
                      handleZoomOut();
                    }}
                  >
                    <Icon name="zoom_out" />
                    <span className="sr-only">Zoom out</span>
                  </Button>
                  <p>
                    <span className="sr-only">Zoom: </span>
                    {zoom}x
                  </p>
                  <Button
                    dark
                    onClick={(e) => {
                      stopPropagation(e);
                      handleZoomIn();
                    }}
                  >
                    <Icon name="zoom_in" />
                    <span className="sr-only">Zoom in</span>
                  </Button>
                </div>
              )}
              {showRotateButton && (
                <div className={styles.zoomControls}>
                  <Button
                    dark
                    onClick={(e) => {
                      stopPropagation(e);
                      setRotation(rotation - 90);
                    }}
                  >
                    <Icon name="rotate_left" />
                    <span className="sr-only">
                      {translations.rotateAntiClockwiseAriaLabel ??
                        "Rotate left"}
                    </span>
                  </Button>
                  <Button
                    dark
                    onClick={(e) => {
                      stopPropagation(e);
                      setRotation(rotation + 90);
                    }}
                  >
                    <Icon name="rotate_right" />
                    <span className="sr-only">
                      {translations.rotateClockwiseAriaLabel ?? "Rotate right"}
                    </span>
                  </Button>
                </div>
              )}
              <Button
                dark
                className={styles.cancelButton}
                onClick={(e) => {
                  stopPropagation(e);
                  handleCancel();
                }}
              >
                {translations.editCancelText}
              </Button>
              {removeBackground &&
                imageWithBackground &&
                imageWithoutBackground && (
                  <Toggle
                    style={{ margin: "0 12px" }}
                    label={translations.backgroundToggleText}
                    checked={backgroundChecked}
                    onChange={() => {
                      if (backgroundChecked) {
                        setImage(imageWithoutBackground);
                      } else {
                        setImage(imageWithBackground);
                      }
                      setBackgroundChecked(!backgroundChecked);
                    }}
                  />
                )}
              <Button
                className={styles.finishButton}
                onClick={(e) => {
                  stopPropagation(e);
                  if (displayRotateOnly) {
                    handleDoneRotateOnly();
                  } else {
                    handleDone();
                  }
                }}
                isLoading={processing}
              >
                {translations.editDoneText}
              </Button>
            </div>
          </>
        ) : preview ? (
          <label htmlFor={id} style={style?.previewStyle?.container}>
            <span className="sr-only">
              {translations.replaceImageAriaLabel}
            </span>
            <img
              aria-hidden
              src={preview}
              style={{
                ...style?.previewStyle?.image,
                ...(fileTooLarge
                  ? { outline: "1px solid var(--wlcm-color-danger-red)" }
                  : {}),
              }}
              width="100%"
              onError={() => setPreview("")}
            />
            <div
              className={styles.uploadOverlay}
              style={style?.overlayStyle ?? {}}
            >
              <Icon name="upload" />
              <p>
                <p>Loading...</p>
                <strong>{translations.replaceImageOverlayText}</strong>
              </p>
              <p className={styles.allowedImageFormats}>
                ({allowedFileTypes.join(", ")})
              </p>
              <p className={styles.maxFileSize}>Max {maxFileSizeMB} MB</p>
              {preview !== originalImageUrl && (
                <Button
                  dark
                  onClick={(e) => {
                    stopPropagation(e);
                    setPreview(originalImageUrl);
                    setFileTooLarge(false);
                    if (onUndo) onUndo(originalImageUrl || "");
                  }}
                  icon="undo"
                  className={styles.undoButton}
                >
                  {translations.undoChangeButtonText}
                </Button>
              )}
            </div>
          </label>
        ) : !isDragging ? (
          <label htmlFor={id}>
            {placeholder}
            <div
              className={styles.uploadOverlay}
              style={style?.overlayStyle ?? {}}
            >
              <Icon name="upload" />
              <p>
                <strong>{translations.uploadImageOverlayText}</strong>
              </p>
              <p className={styles.allowedImageFormats}>
                ({allowedFileTypes.join(", ")})
              </p>
              <p className={styles.maxFileSize}>Max {maxFileSizeMB} MB</p>
            </div>
          </label>
        ) : null}
        <input
          key={inputKey}
          type="file"
          onChange={handleFileChange}
          id={id}
          accept={allowedFileTypes.join(", ")}
          multiple={multiple}
          disabled={disabled}
        />
        {loading && (
          <LoadingDots style={{ position: "absolute", bottom: "15%" }} />
        )}
      </div>
      {fileTooLarge && (
        <div style={{ marginTop: "4px", lineHeight: "normal" }}>
          <StatusText fail text={translations.fileTooLargeText} />
        </div>
      )}
      {tips}
    </>
  );
}

export function ImageInput(props: ImageInputProps) {
  return (
    <div
      style={{ display: "contents" }}
      onChange={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ImageCropProvider>
        <InputComponent {...props} />
      </ImageCropProvider>
    </div>
  );
}
